import { Card, CardRank, CardSuit } from "./card";
import { checkHand, PokerHandResult } from "./poker-hand-checker";

export const SUITS: CardSuit[] = ["Clubs", "Diamonds", "Hearts", "Spades"];
export const RANKS: CardRank[] = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "T", "J", "Q", "K"];

export class WaterfallPokerGame {
    readonly deck: Card[];
    readonly stacks: Card[][];
    readonly outOfPlay: Card[] = [];
    discardsLeft = 0;
    cardsLeft = 52;
    points = 0;

    constructor(quick = false, readonly maxDiscards = 3) {
        this.discardsLeft = maxDiscards;
        this.deck = getShuffledDeck();
        this.stacks = getCardStacks(this.deck);

        if (quick) {
            this.outOfPlay = this.deck;
            this.deck = [];
            this.cardsLeft = 25;
        }
    }

    canDiscard(): boolean {
        return this.discardsLeft > 0;
    }

    /**
     * Removes a card from the top of the specified stack
     * @returns True if it is a valid discard
     */
    discard(stackNum: number): boolean {
        if (stackNum >= 0 && stackNum < 5 && this.discardsLeft > 0) {
            const stack = this.stacks[stackNum];
            if (stack.length > 0) {
                // Move discarded card to bottom of deck
                this.deck.push(stack.shift()!);
                // Get card from top of deck
                const nextCard = this.deck.shift()!;
                // Move it to one of the stacks
                this.getStackToAddTo(stackNum).push(nextCard);

                --this.discardsLeft;
                return true;
            }
        }

        return false;
    }

    private getStackToAddTo(stackNum: number): Card[] {
        // When there are still cards left in the deck just add it to the stack that was discarded
        if (this.deck.length > 0) {
            return this.stacks[stackNum];
        }

        // Otherwise find the stack with the lowest number of cards
        let minStack = this.stacks[0];
        for (const stack of this.stacks) {
            if (stack.length < minStack.length) {
                minStack = stack;
            }
        }

        return minStack;
    }
    
    /**
     * Checks for a valid hand without playing the hand
     * @returns The result or undefined is not a valid hand
     */
    checkHand(): PokerHandResult | undefined {
        const hand = [this.stacks[0][0], this.stacks[1][0], this.stacks[2][0], this.stacks[3][0], this.stacks[4][0]];
        return checkHand(hand.filter(c => c !== undefined));
    }

    /**
     * Checks the hand and plays it if valid
     * @returns The result or undefined is not a valid hand
     */
    playHand(): PokerHandResult | undefined {
        const result = this.checkHand();
        // console.log(result);
        if (result) {
            this.points += result.points;
            this.discardsLeft = this.maxDiscards;
            this.cardsLeft -= result.cards.length;
            for (const card of result.cards) {
                // Find the stack the card is in
                const idx = this.stacks.findIndex(c => c[0] && c[0].rank === card.rank && c[0].suit === card.suit);
                if (idx >= 0) {
                    // Remove card
                    this.outOfPlay.push(this.stacks[idx].shift()!);
                    if (this.deck.length > 0) {
                        // Add a card from the deck
                        this.stacks[idx].push(this.deck.shift()!);
                    }
                }
            }

            if (this.deck.length === 0 && this.stacks.reduce((ttl, s) => ttl += s.length, 0) === 0) {
                // Get a bonus if all cards are cleared
                this.points += 100;
                result.bonus = 100;
            }
        }

        return result;
    }
}

function getShuffledDeck(): Card[] {
    const cards: Card[] = [];
    const deck = getNewDeck();
    while (deck.length > 0) {
        const pos = Math.floor(Math.random() * deck.length);
        cards.push(deck.splice(pos, 1)[0]);
    }

    return cards;
}

function getCardStacks(deck: Card[]): Card[][] {
    const stacks: Card[][] = [[], [], [], [], []];
    
    for (const stack of stacks) {
        for (let j = 0; j < 5; j++) {
            stack.push(deck.shift()!);
        }
    }

    return stacks;
}

function getNewDeck(): Card[] {
    const cards: Card[] = [];
    for (const suit of SUITS) {
        let ordinal = 1;
        for (const rank of RANKS) {
            cards.push({
                suit, rank, ordinal
            });
            ++ordinal;
        }
    }

    return cards;
}
